import React from 'react';
import { MarketingMaterial, formatFileSize, getFileIcon, canPreviewFile } from '../../types/marketing-materials';

interface MaterialCardProps {
  material: MarketingMaterial;
  isAdmin?: boolean;
  onEdit?: (material: MarketingMaterial) => void;
  onDelete?: (material: MarketingMaterial) => void;
  onTogglePublished?: (material: MarketingMaterial) => void;
  onDownload?: (material: MarketingMaterial) => void;
  onPreview?: (material: MarketingMaterial) => void;
}

const MaterialCard: React.FC<MaterialCardProps> = ({
  material,
  isAdmin = false,
  onEdit,
  onDelete,
  onTogglePublished,
  onDownload,
  onPreview,
}) => {
  const isImage = material.file_type.startsWith('image/');
  const canPreview = canPreviewFile(material.file_type);

  return (
    <div style={{
      backgroundColor: 'rgba(31, 41, 55, 0.5)',
      border: '1px solid #374151',
      borderRadius: '12px',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#d4af37';
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 10px 20px rgba(212, 175, 55, 0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#374151';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      {/* Thumbnail/Icon */}
      <div style={{
        height: '180px',
        backgroundColor: 'rgba(17, 24, 39, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        {isImage ? (
          <div style={{
            width: '100%',
            height: '100%',
            backgroundImage: `url(${material.file_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }} />
        ) : (
          <div style={{ fontSize: '64px' }}>
            {getFileIcon(material.file_type)}
          </div>
        )}

        {/* Published Badge */}
        {isAdmin && !material.is_published && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            padding: '4px 12px',
            backgroundColor: 'rgba(239, 68, 68, 0.9)',
            color: 'white',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 'bold',
          }}>
            UNPUBLISHED
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '16px' }}>
        {/* Title */}
        <h3 style={{
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#ffffff',
          marginBottom: '8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {material.title}
        </h3>

        {/* Description */}
        {material.description && (
          <p style={{
            fontSize: '13px',
            color: '#ffffff',
            marginBottom: '12px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '36px',
          }}>
            {material.description}
          </p>
        )}

        {/* Category */}
        {material.category && (
          <div style={{
            display: 'inline-block',
            padding: '4px 10px',
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#d4af37',
            marginBottom: '12px',
          }}>
            {material.category.name}
          </div>
        )}

        {/* File Info */}
        <div style={{
          display: 'flex',
          gap: '12px',
          fontSize: '14px',
          color: '#ffffff',
          marginBottom: '16px',
        }}>
          <span>{formatFileSize(material.file_size)}</span>
          <span>•</span>
          <span>{material.file_type.split('/')[1].toUpperCase()}</span>
          {isAdmin && (
            <>
              <span>•</span>
              <span>{material.download_count} downloads</span>
            </>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Download Button */}
          {onDownload && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownload(material);
              }}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: '#d4af37',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '13px',
              }}
            >
              Download
            </button>
          )}

          {/* Preview Button */}
          {canPreview && onPreview && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPreview(material);
              }}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: 'rgba(55, 65, 81, 0.5)',
                color: '#ffffff',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Preview
            </button>
          )}

          {/* Admin Actions */}
          {isAdmin && (
            <>
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(material);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#60a5fa',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Edit
                </button>
              )}

              {onTogglePublished && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTogglePublished(material);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: material.is_published 
                      ? 'rgba(251, 191, 36, 0.1)' 
                      : 'rgba(34, 197, 94, 0.1)',
                    color: material.is_published ? '#fbbf24' : '#4ade80',
                    border: `1px solid ${material.is_published ? 'rgba(251, 191, 36, 0.3)' : 'rgba(34, 197, 94, 0.3)'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {material.is_published ? 'Hide' : 'Publish'}
                </button>
              )}

              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(material);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#f87171',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialCard;

